import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import BountyPeoplePicker from './BountyPeoplePicker';
import './AdminBadgePanel.css';

const EMPTY_FORM = { id: '', name: '', description: '', icon: '🏆', color: '#f59e0b', status: 'ACTIVE' };

// 後台「徽章管理」分頁：上半為徽章收集功能總開關，下半為自訂榮譽徽章的建立/編輯/頒發/撤銷。
// 自訂榮譽徽章與既有計算型成就徽章獨立並存，這裡只管理「手動頒發」的榮譽徽章。
const AdminBadgePanel = () => {
  const { settings, updateSetting } = useSettings();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingBusy, setSettingBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [pickerBadge, setPickerBadge] = useState(null);
  const [recipientsOf, setRecipientsOf] = useState(null);
  const [recipients, setRecipients] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    adminService.badges.list()
      .then((res) => setBadges(res.data || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // 徽章收集功能總開關（沿用 system_settings：badge_collection_enabled，預設開啟）
  const collectionOn = settings.badge_collection_enabled !== 'false';
  const toggleCollection = async () => {
    setSettingBusy(true);
    setError('');
    try {
      await updateSetting('badge_collection_enabled', collectionOn ? 'false' : 'true');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSettingBusy(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (b) => {
    setForm({ id: b.id, name: b.name, description: b.description || '', icon: b.icon || '🏆', color: b.color || '#f59e0b', status: b.status || 'ACTIVE' });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('請填寫徽章名稱'); return; }
    setError('');
    try {
      if (form.id) {
        await adminService.badges.update(form.id, form);
      } else {
        await adminService.badges.create(form);
      }
      setShowForm(false);
      load();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const remove = async (b) => {
    if (!window.confirm(`確定刪除徽章「${b.name}」？已頒發的紀錄也會一併移除。`)) return;
    try { await adminService.badges.delete(b.id); load(); }
    catch (err) { setError(err.response?.data?.error || err.message); }
  };

  // 頒發：以人員選擇器（多選）挑出對象，呼叫後端頒發並重新載入人數
  const award = async (users) => {
    const ids = (users || []).map((u) => u.id);
    setPickerBadge(null);
    if (!ids.length) return;
    try {
      const res = await adminService.badges.award(pickerBadge.id, ids);
      load();
      window.alert(res.data?.message || `已頒發給 ${res.data?.awarded ?? ids.length} 人`);
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const viewRecipients = async (b) => {
    if (recipientsOf === b.id) { setRecipientsOf(null); setRecipients([]); return; }
    setRecipientsOf(b.id);
    try { const res = await adminService.badges.recipients(b.id); setRecipients(res.data || []); }
    catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const revoke = async (badgeId, userId) => {
    if (!window.confirm('確定撤銷此使用者的徽章？')) return;
    try {
      await adminService.badges.revoke(badgeId, userId);
      setRecipients((prev) => prev.filter((r) => r.user_id !== userId));
      load();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  return (
    <div className="admin-badge-panel">
      <h2>徽章功能設定</h2>
      <div className="badge-setting-list">
        <label className="badge-setting-row">
          <div className="setting-text">
            <div className="setting-name">徽章收集功能</div>
            <div className="setting-desc">關閉後，個人資料頁將不顯示「成就徽章」區塊（計算型徽章）。自訂榮譽徽章不受此開關影響。</div>
          </div>
          <input type="checkbox" className="badge-switch" checked={collectionOn} disabled={settingBusy} onChange={toggleCollection} />
        </label>
      </div>

      <div className="badge-head">
        <h2>自訂榮譽徽章</h2>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>＋ 新增徽章</button>
      </div>
      <p className="badge-hint">由管理者建立並手動頒發的榮譽徽章（例如：本月 MVP、救火英雄）。頒發後會通知獲得者，並顯示於其個人資料頁。</p>
      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="badge-loading">載入中...</div>
      ) : (
        <table className="admin-table badge-table">
          <thead>
            <tr><th>徽章</th><th>說明</th><th>狀態</th><th>已頒發</th><th>操作</th></tr>
          </thead>
          <tbody>
            {badges.length === 0 && <tr><td colSpan={5} className="badge-empty">尚未建立任何榮譽徽章</td></tr>}
            {badges.map((b) => (
              <React.Fragment key={b.id}>
                <tr>
                  <td><span className="badge-chip" style={{ background: b.color || '#f59e0b' }}>{b.icon} {b.name}</span></td>
                  <td className="badge-desc-cell">{b.description}</td>
                  <td>{b.status === 'ACTIVE' ? '啟用' : '停用'}</td>
                  <td>
                    <button className="badge-link" onClick={() => viewRecipients(b)}>{b.recipient_count || 0} 人</button>
                  </td>
                  <td className="badge-actions">
                    <button className="btn btn-sm" onClick={() => setPickerBadge(b)}>頒發</button>
                    <button className="btn btn-sm" onClick={() => openEdit(b)}>編輯</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(b)}>刪除</button>
                  </td>
                </tr>
                {recipientsOf === b.id && (
                  <tr className="badge-recipients-row">
                    <td colSpan={5}>
                      {recipients.length === 0 ? <span className="badge-empty">尚未頒發給任何人</span> : (
                        <div className="badge-recipients">
                          {recipients.map((r) => (
                            <span key={r.id} className="badge-recipient">
                              {r.display_name}
                              <button className="badge-revoke" title="撤銷" onClick={() => revoke(b.id, r.user_id)}>✕</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="badge-form-backdrop" role="dialog" aria-modal="true">
          <div className="badge-form-card">
            <h3>{form.id ? '編輯徽章' : '新增徽章'}</h3>
            <label className="badge-field"><span>名稱</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例如：本月 MVP" />
            </label>
            <label className="badge-field"><span>說明</span>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="頒發此徽章的條件或意義" />
            </label>
            <div className="badge-field-row">
              <label className="badge-field"><span>圖示</span>
                <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="🏆" maxLength={4} />
              </label>
              <label className="badge-field"><span>顏色</span>
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
              </label>
              <label className="badge-field"><span>狀態</span>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="ACTIVE">啟用</option>
                  <option value="DISABLED">停用</option>
                </select>
              </label>
            </div>
            <div className="badge-preview">預覽：<span className="badge-chip" style={{ background: form.color }}>{form.icon} {form.name || '徽章名稱'}</span></div>
            <div className="badge-form-actions">
              <button className="btn" onClick={() => setShowForm(false)}>取消</button>
              <button className="btn btn-primary" onClick={save}>儲存</button>
            </div>
          </div>
        </div>
      )}

      <BountyPeoplePicker
        open={Boolean(pickerBadge)}
        mode="multi"
        title={pickerBadge ? `頒發「${pickerBadge.name}」給` : '頒發徽章'}
        confirmLabel="頒發"
        onClose={() => setPickerBadge(null)}
        onConfirm={award}
      />
    </div>
  );
};

export default AdminBadgePanel;
